/* Wat verder aan het project hangt: businesscase, Terrein, subsidie-intake,
 * inspecties, de Site. Alleen wat er is; een leeg vak voor iets dat niet bestaat
 * voegt niets toe. */

import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { Gekoppeld } from '../data/project';
import { datum, euro } from './opmaak';

interface Kaart {
  sleutel: string;
  label: string;
  waarde: string;
  onder: string | null;
  /** Waar de kaart heen gaat; zonder bestemming is het alleen een feit. */
  naar: string | null;
}

export function gekoppeldeKaarten(g: Gekoppeld): Kaart[] {
  const k: Kaart[] = [];
  if (g.businesscase) {
    const bc = g.businesscase;
    k.push({
      sleutel: 'bc',
      label: 'Businesscase',
      waarde: bc.nettoInvestering ? `${euro(bc.nettoInvestering)} netto` : bc.name ?? 'Businesscase',
      onder: bc.investering ? `investering ${euro(bc.investering)}` : null,
      naar: `/crm/businesscases/${bc.id}`,
    });
  }
  for (const t of g.terreinen) {
    k.push({
      sleutel: `t:${t.id}`,
      label: 'Terrein',
      waarde: `${t.name ?? 'Terrein'} · ${t.ontwerpen} ontwerp${t.ontwerpen === 1 ? '' : 'en'}`,
      onder: t.updated_at ? `bijgewerkt ${datum(t.updated_at)}` : null,
      naar: `/project-builder/${t.id}`,
    });
  }
  for (const s of g.subsidies) {
    k.push({
      sleutel: `s:${s.id}`,
      label: 'Subsidie-intake',
      waarde: s.naam ?? 'Subsidie-intake',
      onder: s.volledigheid != null ? `${s.volledigheid}% ingevuld` : null,
      naar: '/crm/subsidies',
    });
  }
  if (g.inspecties.aantal > 0) {
    const l = g.inspecties.laatste;
    k.push({
      sleutel: 'insp',
      label: 'Inspecties',
      waarde: `${g.inspecties.aantal} uitgevoerd`,
      onder: l ? `laatste ${datum(l.date)}${l.type ? ` · ${l.type.toLowerCase()}` : ''}` : null,
      naar: null,
    });
  }
  if (g.site) {
    k.push({ sleutel: 'site', label: 'Locatie in beheer', waarde: g.site.name ?? 'Site', onder: 'installatie en klantportaal', naar: `/crm/sites/${g.site.id}` });
  }
  return k;
}

function Inhoud({ k, link }: { k: Kaart; link: boolean }) {
  return (
    <>
      <span className="w-28 shrink-0 text-xs text-muted-foreground">{k.label}</span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold">{k.waarde}</span>
        {k.onder && <span className="truncate text-xs text-muted-foreground">{k.onder}</span>}
      </span>
      {link && <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />}
    </>
  );
}

export function GekoppeldBlok({ gekoppeld }: { gekoppeld: Gekoppeld }) {
  const kaarten = gekoppeldeKaarten(gekoppeld);
  if (kaarten.length === 0) return null;
  return (
    <section id="gekoppeld" aria-labelledby="pagina-gekoppeld" className="flex scroll-mt-14 flex-col gap-3">
      <h2 id="pagina-gekoppeld" className="text-lg font-semibold">Gekoppeld</h2>
      <ul className="divide-y rounded-xl border bg-background">
        {kaarten.map((k) => (
          <li key={k.sleutel}>
            {k.naar ? (
              <Link to={k.naar} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50">
                <Inhoud k={k} link />
              </Link>
            ) : (
              <div className="flex items-center gap-3 px-4 py-2.5">
                <Inhoud k={k} link={false} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
